/**
 * Permission helpers — espelham a matriz de @Roles() do backend.
 * Mantenha sincronizado com os controllers correspondentes; serve
 * apenas para UX (esconder link/botão), pois o backend é a verdade.
 */
import type { AuthUser } from '../features/auth/AuthContext';

type Role = AuthUser['role'] | 'manager';

/**
 * Roles autorizados a listar/abrir o módulo Arquivos.
 * Espelha attachments.controller.ts → @Roles em GET /attachments.
 */
const ATTACHMENTS_READ_ROLES: Role[] = [
  'owner',
  'manager',
  'dentist',
  'assistant',
  'receptionist',
];

export function canAccessAttachments(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  return ATTACHMENTS_READ_ROLES.includes(user.role as Role);
}

/**
 * Acesso por PÁGINA (sidebar + route guard) — espelha os @Roles() dos
 * controllers correspondentes (2026-06-12). Páginas fora do mapa são
 * liberadas pra qualquer usuário autenticado (agenda, pacientes,
 * faturas, consentimento… têm rotas para todos os papéis).
 *
 * Auditoria que originou isto: assistente via Configurações/Integrações
 * no sidebar — clicava e tomava 403 do backend. O filtro do menu + o
 * guard de rota alinham a UX à matriz real de permissões.
 */
export const PAGE_ACCESS: Record<string, Role[]> = {
  // tenants PATCH / tenant-integrations / reports / campaigns /
  // message-templates (gestão) / users (gestão) → owner+manager.
  '/configuracoes': ['owner', 'manager'],
  '/integracoes': ['owner', 'manager'],
  '/equipe': ['owner', 'manager'],
  '/relatorios': ['owner', 'manager'],
  '/campanhas': ['owner', 'manager'],
  '/templates': ['owner', 'manager'],
  // marketplace.controller → owner+manager+dentist.
  '/marketplace': ['owner', 'manager', 'dentist'],
  // records.controller → owner+dentist (conteúdo clínico).
  '/prontuario': ['owner', 'dentist'],
  // treatment-plans.controller → owner+manager+dentist.
  '/planos': ['owner', 'manager', 'dentist'],
};

export function canAccessPage(
  user: AuthUser | null | undefined,
  path: string,
): boolean {
  const roles = PAGE_ACCESS[path];
  if (!roles) return true; // página sem restrição específica
  if (!user) return false;
  return roles.includes(user.role as Role);
}


// ─── RBAC Fase 2: gating por PERMISSÃO (não por papel) ──────────────────
// A sidebar e os guards agora consultam as permissões efetivas
// (usePermissions → GET /roles/my-permissions), que resolvem papel custom
// por tenant. Página fora do mapa = aberta a qualquer autenticado.

export type Can = (key: string) => boolean;

/** Permissão exigida por página. Ausente = página aberta. */
export const PAGE_PERMISSION: Record<string, string> = {
  '/agenda': 'appointments.read',
  '/atendimentos-ia': 'agent.read',
  '/crm': 'crm.read',
  '/pacientes': 'patients.read',
  '/planos': 'plans.read',
  '/faturas': 'invoices.read',
  '/estoque': 'inventory.read',
  '/equipe': 'team.read',
  '/prontuario': 'records.read',
  '/arquivos': 'documents.read',
  '/consentimento': 'consents.read',
  '/relatorios': 'reports.read',
  '/conciliacao': 'reconciliation.read',
  '/contas-a-pagar': 'finance.read',
  '/outras-receitas': 'finance.read',
  '/campanhas': 'campaigns.manage',
  '/templates': 'settings.manage',
  '/marketplace': 'marketplace.manage',
  '/configuracoes': 'settings.manage',
  '/integracoes': 'integrations.manage',
};

/** Ordem de fallback pra escolher a landing quando /painel não é acessível. */
const NAV_ORDER = [
  '/painel', '/agenda', '/crm', '/pacientes', '/servicos', '/planos', '/faturas',
  '/equipe', '/prontuario', '/arquivos', '/consentimento', '/relatorios',
  '/suporte', '/ajuda',
];

/** True se o usuário tem a permissão exigida pela página (ou é aberta). */
export function canAccessPageByPerm(can: Can, path: string): boolean {
  const key = PAGE_PERMISSION[path];
  return !key || can(key);
}

/** Primeira página acessível — landing segura quando cai num /painel sem permissão. */
export function firstAccessiblePath(can: Can): string {
  for (const p of NAV_ORDER) if (canAccessPageByPerm(can, p)) return p;
  return '/ajuda';
}
